import type { OreGradeState } from '@/versions/v1/schema/capacity';
import type { CapacityRead } from '@/versions/v1/schema/capacity';
import { CURRENCY_GLYPH } from '@/versions/v1/shared/money';

export type GradeControl = {
  key: keyof OreGradeState;
  label: string;
  value: number;
  display: string;
  min: number;
  max: number;
  step: number;
  minLabel: string;
  maxLabel: string;
  fromInput: (value: number) => number;
  toInput: (value: number) => number;
  /** Track fill, 0–100, from the slider's own range. */
  fillPct: number;
  /** True once the slider has left the measured grade (A9's what-if). */
  moved: boolean;
  /** The measured grade as a tick on the track while the slider is moved. */
  markPct?: number;
  markLabel?: string;
};

export type ArsenicBadge = {
  label: string;
  tone: 'success' | 'warning' | 'danger';
};

type GradeControlBase = Omit<GradeControl, 'fillPct' | 'moved' | 'markPct' | 'markLabel'>;

function trackPct(control: GradeControlBase, value: number): number {
  const position = ((control.toInput(value) - control.min) / (control.max - control.min)) * 100;
  return Math.max(0, Math.min(100, position));
}

/**
 * Grade sliders. With the measured grade supplied, a moved slider carries the
 * measured value as a tick and caption (A9); without it every slider reads at rest.
 */
export function gradeControls(grade: OreGradeState, measured?: OreGradeState): readonly GradeControl[] {
  return gradeControlBases(grade).map((control) => {
    const measuredValue = measured?.[control.key];
    const moved = measuredValue !== undefined && Math.abs(control.value - measuredValue) > 1e-9;
    return {
      ...control,
      fillPct: trackPct(control, control.value),
      moved,
      ...(moved && measuredValue !== undefined ? {
        markPct: trackPct(control, measuredValue),
        markLabel: `measured ${gradeBaseDisplay(control.key, measuredValue)}`,
      } : {}),
    };
  });
}

function gradeBaseDisplay(key: keyof OreGradeState, value: number): string {
  if (key === 'agOzPerShortTon') return value.toFixed(2);
  if (key === 'as') return `${(value * 100).toFixed(3)}%`;
  return `${(value * 100).toFixed(1)}%`;
}

function gradeControlBases(grade: OreGradeState): readonly GradeControlBase[] {
  return [
    {
      key: 'zn',
      label: 'Zn grade',
      value: grade.zn,
      display: `${(grade.zn * 100).toFixed(1)}%`,
      min: 30,
      max: 130,
      step: 1,
      minLabel: '3.0%',
      maxLabel: '13.0%',
      fromInput: (value) => value / 1000,
      toInput: (value) => Math.round(value * 1000),
    },
    {
      key: 'pb',
      label: 'Pb grade',
      value: grade.pb,
      display: `${(grade.pb * 100).toFixed(1)}%`,
      min: 10,
      max: 90,
      step: 2,
      minLabel: '1.0%',
      maxLabel: '9.0%',
      fromInput: (value) => value / 1000,
      toInput: (value) => Math.round(value * 1000),
    },
    {
      key: 'agOzPerShortTon',
      label: 'Ag oz/st',
      value: grade.agOzPerShortTon,
      display: grade.agOzPerShortTon.toFixed(2),
      min: 10,
      max: 300,
      step: 5,
      minLabel: '0.10',
      maxLabel: '3.00',
      fromInput: (value) => value / 100,
      toInput: (value) => Math.round(value * 100),
    },
    {
      key: 'as',
      label: 'As grade',
      value: grade.as,
      display: `${(grade.as * 100).toFixed(3)}%`,
      min: 0,
      max: 100,
      step: 5,
      minLabel: '0.000%',
      maxLabel: '0.100%',
      fromInput: (value) => value / 100000,
      toInput: (value) => Math.round(value * 100000),
    },
  ];
}

function badgeTone(assayPct: number, thresholdPct: number): ArsenicBadge['tone'] {
  if (assayPct >= thresholdPct) return 'danger';
  if (assayPct >= thresholdPct * 0.85) return 'warning';
  return 'success';
}

export function arsenicBadges(read: CapacityRead): readonly ArsenicBadge[] {
  const badges: ArsenicBadge[] = [
    {
      label: `Pb con As ${read.arsenic.leadConcentratePct.toFixed(3)}%`,
      tone: badgeTone(read.arsenic.leadConcentratePct, read.arsenic.leadThresholdPct),
    },
    {
      label: `Zn con As ${read.arsenic.zincConcentratePct.toFixed(3)}%`,
      tone: badgeTone(read.arsenic.zincConcentratePct, read.arsenic.zincThresholdPct),
    },
  ];
  if (read.arsenic.penaltyA > 0) {
    badges.push({ label: `As penalty ${formatCurrency(read.arsenic.penaltyA)}/day`, tone: 'danger' });
  }
  return badges;
}

function formatCurrency(value: number): string {
  return `${CURRENCY_GLYPH}${Math.round(value).toLocaleString('en-US')}`;
}
